import React from 'react';
import { siteConfig } from './config';

const AboutPage = ({ isDarkMode = false }) => {
  return (
    <div className={`min-h-screen font-sans ${isDarkMode ? 'bg-gray-900' : ''}`} style={!isDarkMode ? { backgroundColor: '#f6f2ec' } : {}}>
      {/* Hero Section */}
      <div className="relative h-[400px] w-full overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80')"
          }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-40"></div>
          <div className="relative h-full flex items-center justify-center">
            <div className="text-center text-white px-4">
              <h1 className="text-5xl md:text-6xl font-bold mb-4 leading-tight">
                About J&B Lodge
              </h1>
              <p className="text-xl md:text-2xl font-medium opacity-90">
                Your Hosts, Jennifer and Barry Stevens
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-[80rem] mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Content (2/3 width) */}
          <div className="lg:col-span-2">
            <div className={`rounded-lg shadow-lg p-8 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              {/* TL;DR Summary */}
              <div className={`mb-8 p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-black'}`}>Quick Overview</h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-black'}`}>
                  J&B Lodge offers a private self-catering unit with mountain views, 1km from Underberg. Perfect for couples seeking peace and adventure in the Drakensberg.
                </p>
              </div>

              {/* About Our Lodge */}
              <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>About Our Lodge</h2>
              <p className={`text-lg leading-relaxed mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                J&B Lodge is a self-catering accommodation in Underberg, South Africa, offering mountain views and personalized hospitality. Your hosts, Jennifer and Barry Stevens, welcome you to their peaceful retreat in the Southern Drakensberg.
              </p>

              {/* Your Accommodation */}
              <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Your Accommodation</h2>
              <p className={`text-lg leading-relaxed mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Our charming self-catering unit sits 1km outside Underberg on the Kokstad Road. You'll enjoy stunning mountain views of Hodgson's Peaks and Sani Pass from your private deck. The unit features one bedroom, a private bathroom, a fully equipped kitchen, and an open-plan living area with full HD DStv access. <a href="/gallery" className={`font-semibold ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'} underline transition-colors duration-200`}>View our photo gallery</a> to see the beautiful surroundings and self-catering accommodation.
              </p>

              {/* Location & Activities */}
              <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Location & Activities</h2>
              <p className={`text-lg leading-relaxed mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                The lounge area leads out onto a sunny deck with lovely views. There's undercover parking available with a separate entrance. Relax by the fireplace in the lounge area on a cold winter's night, or enjoy a sundowner on the deck while watching the sun fade behind the mountains.
              </p>
              <p className={`text-lg leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                The Underberg Country Club is only a short drive away for a round of golf, and you're perfectly positioned for exploring the Drakensberg mountains and local attractions.
              </p>
            </div>
          </div>

          {/* Right Column - Meet Jennifer & Barry (1/3 width) */}
          <div className="lg:col-span-1">
            <div className={`rounded-lg shadow-lg p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <h2 className={`text-2xl font-bold mb-6 text-center ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Meet Jennifer & Barry</h2>
              
              {/* Barry Stevens */}
              <div className="mb-6">
                <div className="text-center mb-4">
                  <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                    </svg>
                  </div>
                  <h3 className={`text-lg font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Barry Stevens</h3>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Owner & Host</p>
                </div>
                <div className="space-y-3 text-center">
                  <div>
                    <a href="tel:+27337011689" className={`text-base font-semibold ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'} transition-colors duration-200`}>
                      +27 33 701 1689
                    </a>
                  </div>
                  <div>
                    <a href="tel:+27837944711" className={`text-base font-semibold ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'} transition-colors duration-200`}>
                      +27 83 794 4711
                    </a>
                  </div>
                </div>
              </div>

              {/* Jennifer Stevens */}
              <div className={`border-t pt-6 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="text-center mb-4">
                  <div className="w-20 h-20 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-10 h-10 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                    </svg>
                  </div>
                  <h3 className={`text-lg font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Jennifer Stevens</h3>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Owner & Host</p>
                </div>
                <div className="space-y-3 text-center">
                  <div>
                    <a href="tel:+27337011689" className={`text-base font-semibold ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'} transition-colors duration-200`}>
                      +27 33 701 1689
                    </a>
                  </div>
                  <div>
                    <a href="tel:+27839639816" className={`text-base font-semibold ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'} transition-colors duration-200`}>
                      +27 83 963 9816
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage; 